/**
 * sharePayload — client-side decoder for the public `/share/$token` route.
 *
 * The share preview is intentionally backend-light: the token encodes
 * everything the public card needs to render. We accept two formats:
 *
 *   1. **Base64URL JSON** — the canonical form. The token is a
 *      base64url-encoded JSON document matching `SharePayload`.
 *      Backend services (Kairos) emit these when the user creates a
 *      share link.
 *
 *   2. **`demo` / `sample` sentinels** — render a representative safe
 *      card with no backend, so visual captures and the marketing CTA
 *      work in any environment.
 *
 * ── PRIVACY CONTRACT (load-bearing) ──────────────────────────────────
 * The token carries COUNTS and LABELS only. By construction this decoder
 * NEVER reads, copies, or surfaces:
 *   - monetary amounts (R$/BRL or any number-as-money);
 *   - vendor / bill / account names;
 *   - e-mail addresses or any user id.
 * The result `payload` is built field-by-field from an allow-list
 * (`v`, `variant`, `expiresAt`, `visibility`, `monthLabel`, `billsCount`,
 * `paidCount`, `milestoneLabel`). Any EXTRA field on a crafted token is
 * silently dropped — it can never reach the rendered card. The covering
 * test is `__tests__/sharePayload.test.ts` ("privacy guard").
 *
 * ── KAIROS GAP (do not fake) ─────────────────────────────────────────
 * A *server-resolved* preview is NOT possible today. Kairos' only public
 * resolver is `GET /s/{short_id}`, which 302-REDIRECTS and returns no
 * JSON body — so there is nothing the web can fetch to render a card.
 * Until Kairos ships a public JSON endpoint
 *
 *     GET /v1/share/{short_id}  ->  ShareArtifact  (from kairos'
 *                                   `build_share_artifact`, safe fields
 *                                   only: variant / labels / counts)
 *
 * the web uses this self-contained client-side decoder. When that
 * endpoint lands, this file is the ONLY place that needs to learn the
 * network call (swap `decodeShareToken` for an async resolver that maps
 * the JSON ShareArtifact onto `SharePayload`). The versioned `v: 1`
 * envelope means old base64url links keep degrading gracefully.
 */

import type {
  SafeShareVariant,
  ShareVisibility,
} from '@francisquiniai/cuidari-core/share'

export interface SharePayload {
  /** Format version; bump when changing the encoding. */
  v: 1
  /** Which variant of safe-share this is (clean-month, streak, etc.). */
  variant: SafeShareVariant
  /** ISO8601 expiration timestamp; null = never expires. */
  expiresAt: string | null
  /** Visibility level set by the sharer. */
  visibility?: ShareVisibility
  /** Optional human label for the month (e.g. "Maio 2026"). */
  monthLabel?: string | null
  /** Optional bills-count to drive the preview body. */
  billsCount?: number | null
  /** Optional paid-count to drive the preview body. */
  paidCount?: number | null
  /** Optional milestone label (when variant === 'milestone'). */
  milestoneLabel?: string | null
}

/**
 * Discriminated result. `payload` is non-null iff `kind === 'valid'`,
 * so callers get exhaustive, type-safe branching:
 *   - `valid`   → render the safe card from `payload`.
 *   - `expired` → token parsed but its `expiresAt` is in the past.
 *   - `invalid` → missing / malformed / wrong-version / unknown-variant.
 */
type SharePayloadResult =
  | { kind: 'valid'; payload: SharePayload }
  | { kind: 'expired'; payload: null }
  | { kind: 'invalid'; payload: null }

const VALID_VARIANTS: readonly SafeShareVariant[] = [
  'clean-month',
  'streak',
  'delta',
  'milestone',
]

const VALID_VISIBILITIES: readonly ShareVisibility[] = [
  'public',
  'private-link',
  'contacts',
  'draft',
]

const INVALID: SharePayloadResult = { kind: 'invalid', payload: null }
const EXPIRED: SharePayloadResult = { kind: 'expired', payload: null }

function base64UrlDecode(token: string): string | null {
  // base64url → base64
  const b64 = token.replace(/-/g, '+').replace(/_/g, '/')
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4))
  try {
    if (typeof atob === 'function') {
      // jsdom + browsers
      return atob(b64 + pad)
    }
    // Node fallback (unit tests in environments without `atob`).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const buf = (globalThis as any).Buffer
    if (buf && typeof buf.from === 'function') {
      return buf.from(b64 + pad, 'base64').toString('utf-8')
    }
  } catch {
    return null
  }
  return null
}

function isFinitePositiveInt(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0
}

/**
 * Decode a share token into a discriminated result. Returns `'invalid'`
 * for malformed tokens, `'expired'` for tokens whose `expiresAt` is in
 * the past, `'valid'` otherwise. Never throws — every error path returns
 * a structured result the UI can render.
 *
 * Privacy: the returned `payload` is assembled from a fixed allow-list of
 * safe fields. Unknown / extra keys on the input are ignored.
 */
export function decodeShareToken(
  token: string | null | undefined,
  now: Date = new Date(),
): SharePayloadResult {
  if (!token || typeof token !== 'string') {
    return INVALID
  }

  // `demo` / `sample` sentinels render a representative safe card with no
  // backend — used by visual captures and the marketing CTA. Harmless if
  // left once Kairos ships real tokens.
  if (token === 'demo' || token === 'sample') {
    return {
      kind: 'valid',
      payload: {
        v: 1,
        variant: 'clean-month',
        expiresAt: null,
        monthLabel: 'mês cuidado',
      },
    }
  }

  const decoded = base64UrlDecode(token)
  if (!decoded) return INVALID

  let raw: unknown
  try {
    raw = JSON.parse(decoded)
  } catch {
    return INVALID
  }

  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return INVALID
  }
  const obj = raw as Record<string, unknown>

  if (obj.v !== 1 || typeof obj.variant !== 'string') {
    return INVALID
  }
  const variant = obj.variant as SafeShareVariant
  if (!VALID_VARIANTS.includes(variant)) {
    return INVALID
  }

  const expiresAt = typeof obj.expiresAt === 'string' ? obj.expiresAt : null
  if (expiresAt) {
    const exp = Date.parse(expiresAt)
    if (!Number.isNaN(exp) && exp < now.getTime()) {
      return EXPIRED
    }
  }

  // Build the payload field-by-field from the safe allow-list. Anything
  // else on `obj` (amounts, vendor names, emails, ids, …) is dropped here
  // and can never reach the rendered card.
  const payload: SharePayload = {
    v: 1,
    variant,
    expiresAt,
    visibility: VALID_VISIBILITIES.includes(obj.visibility as ShareVisibility)
      ? (obj.visibility as ShareVisibility)
      : undefined,
    monthLabel:
      typeof obj.monthLabel === 'string' || obj.monthLabel === null
        ? (obj.monthLabel as string | null)
        : undefined,
    billsCount: isFinitePositiveInt(obj.billsCount) ? obj.billsCount : null,
    paidCount: isFinitePositiveInt(obj.paidCount) ? obj.paidCount : null,
    milestoneLabel:
      typeof obj.milestoneLabel === 'string' ? obj.milestoneLabel : null,
  }
  return { kind: 'valid', payload }
}

/**
 * Encode a share payload back to a base64url token. Useful for tests and
 * future Kairos client code that produces tokens client-side.
 */
export function encodeShareToken(payload: SharePayload): string {
  const json = JSON.stringify(payload)
  let b64: string
  if (typeof btoa === 'function') {
    b64 = btoa(json)
  } else {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const buf = (globalThis as any).Buffer
    b64 = buf.from(json, 'utf-8').toString('base64')
  }
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
